import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { PrestadorComposicaoAba, PrestadorComposicaoEspecialidadeResponse, PrestadorComposicaoItemRequest, PrestadorComposicaoItemResponse, PrestadorComposicaoTipo } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.model';
import { PrestadorComposicaoService } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.service';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { COMPOSICAO_FINANCEIRO_RETURN_CONTEXT_ID, PrestadorFinanceiroNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-financeiro-navigation.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { DateUtils } from '@/shared/utils/date-utils';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type ComposicaoTabelaRow = {
    crmUf: string;
    nome: TablePerfilValue;
    vinculo: string;
    periodo: string;
    situacao: string;
    naturezaJuridica: string;
    entrada: string;
    saida: string;
    atualizadoEm: string;
    flags: string;
    source: PrestadorComposicaoItemResponse;
};

@Component({
    selector: 'app-composicao-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ReactiveFormsModule, BotaoAcoes, Button, CardContent, ConfirmDialog, CustomTableComponent, Dialog, InputText, Select, TableModule, TabContainer, TabContent, Pesquisa],
    providers: [ConfirmationService],
    templateUrl: './composicao-prestador.html',
    styleUrls: ['./composicao-prestador.scss']
})
export class ComposicaoPrestador {
    pessoa = input<Pessoa>();

    protected readonly roleComposicaoEditar = PERMISSOES.prestadores.composicao.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        const role = this.roleComposicaoEditar;
        const salvando = this.salvandoTipo();
        return [
            acaoMenuItem({ id: 'novo', label: 'Novo', icon: 'pi pi-plus', cor: 'novo', permission: role, command: () => this.novo() }),
            acaoMenuItem({ id: 'atualizar', label: 'Atualizar', icon: 'pi pi-refresh', cor: 'info', disabled: this.loading(), command: () => this.carregarDados() }),
            acaoMenuItem({ id: 'editar-tipo', label: 'Editar tipo', icon: 'pi pi-pencil', cor: 'editar', disabled: this.editandoTipo || salvando, permission: role, command: () => this.habilitarEdicaoTipo() })
        ];
    }

    /**
     * Ver {@link itensAcoes}: Salvar e Cancelar saem do menu e viram ação em destaque.
     *
     * <p>Aqui o par acompanha `editandoTipo`, e não `editando` — são dois estados distintos neste
     * componente, e é a edição do tipo de corpo clínico que estes botões salvam.
     */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleComposicaoEditar;
        const salvando = this.salvandoTipo();
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: salvando, permission: role, command: () => this.salvarTipoCorpoClinico() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', disabled: salvando, permission: role, command: () => this.cancelarEdicaoTipo() })
        ];
    }

    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fotos = inject(FotoMedicoCacheService);
    private readonly composicaoService = inject(PrestadorComposicaoService);
    private readonly notificacoesService = inject(NotificacoesService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly financeiroNavigationService = inject(PrestadorFinanceiroNavigationService);

    linhas = signal<PrestadorComposicaoItemResponse[]>([]);
    linhasTabela = computed<ComposicaoTabelaRow[]>(() => this.linhas().map((row) => this.toTabelaRow(row)));
    loading = signal(false);
    salvando = signal(false);
    salvandoTipo = signal(false);
    selectedComposicao: PrestadorComposicaoAba = 'ATUAL';
    selectedTipo: PrestadorComposicaoTipo = 'FECHADO';
    tipoOriginal: PrestadorComposicaoTipo = 'FECHADO';
    editandoTipo = false;
    editando = false;
    pesquisaMedicoDialog = false;
    pesquisaPsmtDialog = false;
    especialidadesDialog = false;
    especialidadesLoading = false;
    especialidadesRegistradas: PrestadorComposicaoEspecialidadeResponse[] = [];
    especialidadesDeclaradas: PrestadorComposicaoEspecialidadeResponse[] = [];
    linhaEspecialidades?: PrestadorComposicaoItemResponse;

    composicaoOptions = [
        { label: 'Atual', value: 'ATUAL' },
        { label: 'Antiga', value: 'ANTIGA' }
    ];
    tipoOptions = [
        { label: 'Aberto', value: 'ABERTO' },
        { label: 'Fechado', value: 'FECHADO' },
        { label: 'Misto', value: 'MISTO' }
    ];
    vinculoOptions = [
        { label: 'Associado', value: 'A' },
        { label: 'Contratado', value: 'C' },
        { label: 'Quotista', value: 'Q' },
        { label: 'Autonomo', value: 'T' },
        { label: 'Outros', value: 'O' },
        { label: 'PSMT', value: 'J' },
        { label: 'Sem Vínculo', value: 'S' },
        { label: 'Servidor/Funcionario Publico', value: 'P' }
    ];
    tableColumnOrder = ['crmUf', 'nome', 'vinculo', 'periodo', 'situacao', 'naturezaJuridica', 'entrada', 'saida', 'atualizadoEm', 'flags'];

    tableMetadata: TableMetadata = {
        headers: {
            crmUf: { type: 'text', default: '-', hidden: false, value: 'CRM / UF' },
            nome: { type: 'perfil', default: '-', hidden: false, value: 'Nome' },
            vinculo: { type: 'text', default: '-', hidden: false, value: 'Vínculo' },
            periodo: { type: 'text', default: '-', hidden: false, value: 'Período' },
            situacao: { type: 'text', default: '-', hidden: true, value: 'Situação' },
            naturezaJuridica: { type: 'text', default: '-', hidden: true, value: 'NJ' },
            entrada: { type: 'text', default: '-', hidden: true, value: 'Entrada' },
            saida: { type: 'text', default: '-', hidden: true, value: 'Saída' },
            atualizadoEm: { type: 'text', default: '-', hidden: true, value: 'Dt. Atualização' },
            flags: { type: 'text', default: '-', hidden: true, value: 'Alertas' }
        },
        title: 'nome',
        actions: {
            editar: {
                label: 'Editar',
                icon: 'pi pi-pencil',
                color: 'info',
                command: (item: ComposicaoTabelaRow) => this.editar(item.source),
                permission: PERMISSOES.prestadores.composicao.editar
            },
            especialidades: {
                label: 'Especialidades',
                icon: 'pi pi-list',
                color: 'success',
                command: (item: ComposicaoTabelaRow) => this.abrirEspecialidades(item.source)
            },
            informacoesFinanceiras: {
                label: 'Informações financeiras',
                icon: 'pi pi-dollar',
                color: 'primary',
                command: (item: ComposicaoTabelaRow) => this.abrirInformacoesFinanceiras(item.source)
            },
            remover: {
                label: 'Remover',
                icon: 'pi pi-trash',
                color: 'danger',
                command: (item: ComposicaoTabelaRow) => this.confirmarRemocao(item.source),
                permission: PERMISSOES.prestadores.composicao.editar
            }
        },
        defaultSortField: 'nome',
        defaultSortOrder: 1
    };

    form = this.fb.group({
        idMedico: this.fb.control<number | null>(null, Validators.required),
        ufMedico: this.fb.control<string | null>(null, [Validators.required, Validators.pattern(/^[A-Za-z]{2}$/)]),
        medicoNome: this.fb.control<string | null>(null),
        dtEntrada: this.fb.control<string | null>(null, Validators.required),
        dtEntradaOriginal: this.fb.control<string | null>(null),
        dtSaida: this.fb.control<string | null>(null),
        naturezaVinculo: this.fb.control<string | null>(null),
        // Vinculo 'J' (PSMT): o Delphi expoe e exige o prestador do PSMT (UntCadastroCorpoClinico.pas:132/175).
        idSolicitantePsmt: this.fb.control<number | null>(null),
        ufPsmt: this.fb.control<string | null>(null),
        psmtNome: this.fb.control<string | null>(null)
    });

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            untracked(() => {
                if (pessoa?.id && pessoa.uf) {
                    this.carregarDados(pessoa);
                } else {
                    this.linhas.set([]);
                }
            });
        });

        this.form.controls.naturezaVinculo.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vinculo) => this.sincronizarPsmt(vinculo));
    }

    /** Vinculo 'J' (PSMT) exige a selecao do prestador do PSMT. */
    get exigePsmt(): boolean {
        return this.form.controls.naturezaVinculo.value === 'J';
    }

    carregarDados(pessoa: Pessoa = this.pessoa()!): void {
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.loading.set(true);
        this.composicaoService
            .buscar(pessoa, this.selectedComposicao)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.aplicarTipoCorpoClinico(response?.tipoCorpoClinico);
                    this.aplicarLinhas(response?.itens ?? []);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.linhas.set([])
            });
    }

    alternarComposicao(value: string): void {
        this.selectedComposicao = value as PrestadorComposicaoAba;
        this.cancelarEdicao();
        this.carregarDados();
    }

    alternarTipo(value: PrestadorComposicaoTipo): void {
        this.selectedTipo = value;
    }

    habilitarEdicaoTipo(): void {
        this.editandoTipo = true;
    }

    cancelarEdicaoTipo(): void {
        this.selectedTipo = this.tipoOriginal;
        this.editandoTipo = false;
    }

    salvarTipoCorpoClinico(): void {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return;
        }

        this.salvandoTipo.set(true);
        this.composicaoService
            .salvarTipo({ id: pessoa.id, uf: pessoa.uf, tipo: this.selectedTipo })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvandoTipo.set(false))
            )
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe((response) => {
                this.aplicarTipoCorpoClinico(response?.tipoCorpoClinico);
                this.aplicarLinhas(response?.itens ?? []);
                this.editandoTipo = false;
                this.notificacoesService.notificar({ severity: 'success', summary: 'Tipo do corpo clínico salvo.' });
            });
    }

    novo(): void {
        this.editando = true;
        this.form.reset({ dtEntrada: DateUtils.toIsoDate(new Date()), naturezaVinculo: 'C' });
    }

    editar(row: PrestadorComposicaoItemResponse): void {
        this.editando = true;
        // Vinculo 'J' (PSMT): pre-preenche o prestador do PSMT ja gravado para o form nascer valido
        // e permitir editar (ex.: dtSaida) sem reselecionar o PSMT.
        const ehPsmt = row.naturezaVinculo === 'J';
        this.form.reset({
            idMedico: row.idMedico,
            ufMedico: row.ufMedico,
            medicoNome: row.nome ?? null,
            dtEntrada: row.dtEntrada,
            dtEntradaOriginal: row.dtEntrada,
            dtSaida: row.dtSaida ?? null,
            naturezaVinculo: row.naturezaVinculo ?? null,
            idSolicitantePsmt: ehPsmt ? (row.idSolicitantePsmt ?? null) : null,
            ufPsmt: ehPsmt ? (row.ufPsmt ?? null) : null,
            psmtNome: ehPsmt ? (row.psmtNome ?? null) : null
        });
    }

    salvar(): void {
        const pessoa = this.pessoa();
        if (!pessoa || this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const body = this.toRequest(pessoa);
        const request$ = body.dtEntradaOriginal ? this.composicaoService.atualizar(body) : this.composicaoService.incluir(body);
        this.salvando.set(true);
        request$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe((response) => {
                this.aplicarLinhas(response?.itens ?? []);
                this.cancelarEdicao();
                this.notificacoesService.notificar({ severity: 'success', summary: 'Corpo clínico salvo.' });
            });
    }

    removerHistorico(row: PrestadorComposicaoItemResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa || !row.dtSaida) {
            return;
        }
        this.composicaoService
            .removerHistorico({
                id: pessoa.id,
                uf: pessoa.uf,
                idMedico: row.idMedico,
                ufMedico: row.ufMedico,
                dtEntrada: row.dtEntrada,
                dtSaida: row.dtSaida
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.carregarDados();
                this.notificacoesService.notificar({ severity: 'success', summary: 'Registro removido.' });
            });
    }

    confirmarRemocao(row: PrestadorComposicaoItemResponse): void {
        this.confirmationService.confirm({
            header: 'Remover médico',
            message: `Deseja remover ${row.nome ?? 'este médico'} do corpo clínico? Eventuais chefias técnicas ativas deste médico neste prestador serão removidas, e os serviços correspondentes poderão ficar sem chefe técnico.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.remover(row)
        });
    }

    remover(row: PrestadorComposicaoItemResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return;
        }
        this.composicaoService
            .remover({
                id: pessoa.id,
                uf: pessoa.uf,
                idMedico: row.idMedico,
                ufMedico: row.ufMedico,
                dtEntrada: row.dtEntrada,
                dtSaida: row.dtSaida ?? null
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.carregarDados();
                this.notificacoesService.notificar({ severity: 'success', summary: 'Registro removido.' });
            });
    }

    cancelarEdicao(): void {
        this.editando = false;
        this.form.reset();
    }

    selecionarMedico(medico: PessoaDomain): void {
        this.form.patchValue({
            idMedico: medico.id,
            ufMedico: medico.uf,
            medicoNome: medico.nome ?? medico.registro ?? null
        });
    }

    selecionarPsmt(prestador: PessoaDomain): void {
        this.form.patchValue({
            idSolicitantePsmt: prestador.id,
            ufPsmt: prestador.uf,
            psmtNome: prestador.nome ?? prestador.registro ?? null
        });
    }

    /**
     * Alterna a obrigatoriedade do prestador do PSMT conforme o vinculo. Para vinculo 'J' o Delphi
     * expoe e exige o PSMT; nos demais vinculos os campos ficam ocultos e sao limpos.
     */
    private sincronizarPsmt(vinculo: string | null): void {
        const idPsmt = this.form.controls.idSolicitantePsmt;
        const ufPsmt = this.form.controls.ufPsmt;

        if (vinculo === 'J') {
            idPsmt.setValidators(Validators.required);
            ufPsmt.setValidators([Validators.required, Validators.pattern(/^[A-Za-z]{2}$/)]);
        } else {
            idPsmt.clearValidators();
            ufPsmt.clearValidators();
            idPsmt.setValue(null, { emitEvent: false });
            ufPsmt.setValue(null, { emitEvent: false });
            this.form.controls.psmtNome.setValue(null, { emitEvent: false });
        }

        idPsmt.updateValueAndValidity({ emitEvent: false });
        ufPsmt.updateValueAndValidity({ emitEvent: false });
    }

    abrirInformacoesFinanceiras(row: PrestadorComposicaoItemResponse): void {
        this.financeiroNavigationService.abrirMedico(
            this.pessoa() as PessoaDomain | undefined,
            { id: row.idMedico, uf: row.ufMedico, crm: row.crm != null ? String(row.crm) : undefined, nome: row.nome ?? undefined, tipo: 'MEDICO' },
            { id: COMPOSICAO_FINANCEIRO_RETURN_CONTEXT_ID, returnUrl: '/prestadores/composicao' }
        );
    }

    abrirEspecialidades(row: PrestadorComposicaoItemResponse): void {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return;
        }
        this.linhaEspecialidades = row;
        this.especialidadesDialog = true;
        this.especialidadesLoading = true;
        this.composicaoService
            .buscarEspecialidades({
                id: pessoa.id,
                uf: pessoa.uf,
                idMedico: row.idMedico,
                ufMedico: row.ufMedico,
                dtEntrada: row.dtEntrada
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => (this.especialidadesLoading = false))
            )
            .subscribe((response) => {
                this.especialidadesRegistradas = response.registradas ?? [];
                this.especialidadesDeclaradas = response.declaradas ?? [];
            });
    }

    formatarData(value?: string | null): string {
        return DateUtils.formatDate(value);
    }

    /**
     * Publica as linhas e dispara os retratos da página. Os três pontos de carga passam por aqui
     * para nenhum deles esquecer a segunda metade — as linhas são `computed` sobre o cache, então
     * cada foto que chega refaz a sua linha sozinha.
     */
    private aplicarLinhas(itens: PrestadorComposicaoItemResponse[]): void {
        this.linhas.set(itens);
        this.fotos.precarregar(itens.map((item) => ({ crm: item.crm, uf: item.ufMedico })));
    }

    private toTabelaRow(row: PrestadorComposicaoItemResponse): ComposicaoTabelaRow {
        const entrada = this.formatarData(row.dtEntrada);
        const saida = row.dtSaida ? this.formatarData(row.dtSaida) : 'Atual';
        return {
            crmUf: `${row.crm ?? '-'} / ${row.ufMedico ?? '-'}`,
            nome: this.fotos.perfilMedico(row.nome, row.crm, row.ufMedico),
            vinculo: row.vinculo ?? '-',
            periodo: `${entrada} até ${saida}`,
            situacao: row.situacao ?? '-',
            naturezaJuridica: row.naturezaJuridica ?? '-',
            entrada,
            saida,
            atualizadoEm: row.dtAtualizacao ? DateUtils.formatDate(row.dtAtualizacao) : '-',
            flags: this.formatarFlags(row),
            source: row
        };
    }

    private aplicarTipoCorpoClinico(tipo: string | null | undefined): void {
        const normalizado = this.normalizarTipoCorpoClinico(tipo);
        this.selectedTipo = normalizado;
        this.tipoOriginal = normalizado;
    }

    private normalizarTipoCorpoClinico(tipo: string | null | undefined): PrestadorComposicaoTipo {
        switch (tipo) {
            case 'A':
            case 'ABERTO':
                return 'ABERTO';
            case 'M':
            case 'MISTO':
                return 'MISTO';
            case 'F':
            case 'FECHADO':
            default:
                return 'FECHADO';
        }
    }

    private formatarFlags(row: PrestadorComposicaoItemResponse): string {
        const flags = [];
        if (row.devedor) {
            flags.push('Médico devedor');
        }
        if (row.falecido) {
            flags.push('Médico falecido');
        }
        if (row.emergencia) {
            flags.push('Emergência');
        }
        return flags.length ? flags.join(', ') : '-';
    }

    private toRequest(pessoa: Pessoa): PrestadorComposicaoItemRequest {
        const value = this.form.getRawValue();
        return {
            id: pessoa.id,
            uf: pessoa.uf,
            idMedico: Number(value.idMedico),
            ufMedico: String(value.ufMedico),
            dtEntrada: String(value.dtEntrada),
            dtEntradaOriginal: value.dtEntradaOriginal,
            dtSaida: value.dtSaida,
            naturezaVinculo: value.naturezaVinculo,
            // Vinculo 'J' (PSMT): envia o prestador do PSMT selecionado; nos demais vinculos os campos ficam nulos.
            idSolicitantePsmt: value.naturezaVinculo === 'J' && value.idSolicitantePsmt != null ? Number(value.idSolicitantePsmt) : null,
            ufPsmt: value.naturezaVinculo === 'J' ? value.ufPsmt : null,
            composicao: this.selectedComposicao,
            tipo: this.selectedTipo
        };
    }
}
